import React, { useMemo, useState, useEffect } from 'react';
import { 
  Building2, 
  Users, 
  Activity,
  Clock,
  PieChart as PieChartIcon,
  BarChart2 as BarChartIcon,
  TrendingUp,
  Calendar,
  CalendarDays,
  UserPlus,
  UserCheck,
  UserX,
  CreditCard
} from 'lucide-react';
import { formatDateTime } from '../../../utils/helperFunction';
import { getUserStatistics } from '../../../api/apiFunction/orgServices';
// Nivo import
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveBar } from '@nivo/bar';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

/**
 * AdminDashboard component displaying organization or user statistics
 * @param {Object} props - Component props
 * @param {Array} props.organizations - List of all organizations
 * @returns {JSX.Element} AdminDashboard component
 */
const AdminDashboard = ({ organizations = [] }) => {
  // State for view mode: 'organizations' or 'users'
  const [viewMode, setViewMode] = useState(() => {
    // Load from localStorage or default to 'organizations'
    const saved = localStorage.getItem('adminDashboardViewMode');
    return saved === 'users' ? 'users' : 'organizations';
  });
  
  // State for chart view type
  const [chartView, setChartView] = useState('daily'); // 'daily' or 'monthly'
  
  // State for user statistics
  const [userStats, setUserStats] = useState(null);
  const [loadingUserStats, setLoadingUserStats] = useState(false);
  
  // Fetch user statistics when in users mode
  useEffect(() => {
    if (viewMode === 'users') {
      fetchUserStatistics();
    }
  }, [viewMode]);
  
  // Save view mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('adminDashboardViewMode', viewMode);
  }, [viewMode]);
  
  const fetchUserStatistics = async () => {
    try {
      setLoadingUserStats(true);
      const response = await getUserStatistics();
      if (response?.data) {
        setUserStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching user statistics:', error);
    } finally {
      setLoadingUserStats(false);
    }
  };

  
  // Calculate statistics
  const totalOrganizations = organizations.length;
  
  // Calculate total employees by summing up employeeCount from all organizations
  const totalEmployees = useMemo(() => {
    return organizations.reduce((sum, org) => {
      const employeeCount = org.employees ? org.employees.length : (org.employeeCount || 0);
      return sum + employeeCount;
    }, 0);
  }, [organizations]);
  
  const avgEmployees = totalOrganizations > 0 ? (totalEmployees / totalOrganizations).toFixed(1) : 0;
  
  // Get active organizations (assuming there's a status field)
  const activeOrganizations = organizations.filter(org => org.status === true).length;
  const activePercentage = totalOrganizations > 0 
    ? Math.round((activeOrganizations / totalOrganizations) * 100) 
    : 0;

  // Get recently created organizations
  const recentOrgs = [...organizations]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 3);

  // Prepare data for charts
  const { chartData, statusData, employeeDistribution } = useMemo(() => {
    const timeData = {};
    const statusCount = { active: 0, inactive: 0 };
    const employeeDist = [];

    organizations.forEach(org => {
      const date = new Date(org.createdAt);
      let timeKey;
      
      if (chartView === 'monthly') {
        // Group by month
        timeKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      } else {
        // Group by day
        timeKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
      }
      
      timeData[timeKey] = (timeData[timeKey] || 0) + 1;
      
      // Status data
      if (org.status) statusCount.active++;
      else statusCount.inactive++;
      
      // Employee distribution (top 5 orgs)
      if (org.employeeCount > 0) {
        employeeDist.push({
          name: org.name,
          employees: org.employeeCount || 0
        });
      }
    });

    // Sort and get top 5 organizations by employee count
    const topOrganizations = [...employeeDist]
      .sort((a, b) => b.employees - a.employees)
      .slice(0, 5);

    // Sort time data chronologically
    const sortedTimeKeys = Object.keys(timeData).sort();
    
    return {
      chartData: {
        labels: sortedTimeKeys,
        values: sortedTimeKeys.map(key => timeData[key])
      },
      statusData: statusCount,
      employeeDistribution: topOrganizations
    };
  }, [organizations, chartView]);

  // Prepare Nivo data for line chart
  const nivoLineData = useMemo(() => [
    {
      id: chartView === 'monthly' ? 'Monthly' : 'Daily',
      color: 'hsl(229, 70%, 50%)',
      data: chartData.labels.map((label, idx) => ({
        x: label,
        y: chartData.values[idx]
      }))
    }
  ], [chartData, chartView]);

  // Prepare Nivo data for pie chart
  const nivoPieData = useMemo(() => [
    { id: 'Active', label: 'Active', value: statusData.active, color: '#10b981' },
    { id: 'Inactive', label: 'Inactive', value: statusData.inactive, color: '#ef4444' }
  ], [statusData]);

  // Prepare Nivo data for bar chart
  const nivoBarData = useMemo(() => employeeDistribution.map(org => ({
    organization: org.name,
    Employees: org.employees
  })), [employeeDistribution]);

  // Chart options and data
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#9CA3AF'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: 14 },
        bodyFont: { size: 12 },
        padding: 10,
        displayColors: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          color: '#9CA3AF'
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#9CA3AF'
        }
      }
    }
  };

  // Time-based organizations chart data
  const timeChartData = {
    labels: chartData.labels,
    datasets: [
      {
        label: `New Organizations (${chartView === 'monthly' ? 'Monthly' : 'Daily'})`,
        data: chartData.values,
        backgroundColor: 'rgba(79, 70, 229, 0.2)',
        borderColor: 'rgba(79, 70, 229, 1)',
        borderWidth: 2,
        tension: 0.3,
        fill: true,
        pointBackgroundColor: 'rgba(79, 70, 229, 1)'
      },
    ],
  };

  // Status pie chart data
  const statusChartData = {
    labels: ['Active', 'Inactive'],
    datasets: [
      {
        data: [statusData.active, statusData.inactive],
        backgroundColor: [
          'rgba(16, 185, 129, 0.8)',
          'rgba(239, 68, 68, 0.8)',
        ],
        borderColor: [
          'rgba(16, 185, 129, 1)',
          'rgba(239, 68, 68, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  // Employee distribution chart data
  const employeeChartData = {
    labels: employeeDistribution.map(org => org.name),
    datasets: [
      {
        label: 'Employees',
        data: employeeDistribution.map(org => org.employees),
        backgroundColor: 'rgba(99, 102, 241, 0.8)',
        borderColor: 'rgba(99, 102, 241, 1)',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  // Helper function to format dates for chart display
  const formatChartDate = (dateString, viewType) => {
    if (!dateString) return '';
    
    if (viewType === 'monthly') {
      // Format: "2025-12" -> "Dec 2025"
      const [year, month] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } else {
      // Format: "2025-12-02" -> "Dec 2" or "12/02"
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // Prepare user statistics data for charts
  const userChartData = useMemo(() => {
    if (!userStats) return { labels: [], values: [] };
    
    const data = chartView === 'monthly' ? userStats.monthlyUserData : userStats.dailyUserData;
    const sortedKeys = Object.keys(data).sort();
    
    return {
      labels: sortedKeys.map(key => formatChartDate(key, chartView)),
      rawLabels: sortedKeys, // Keep raw labels for tooltip
      values: sortedKeys.map(key => data[key])
    };
  }, [userStats, chartView]);

  // User status pie chart data with percentages
  const userStatusPieData = useMemo(() => {
    if (!userStats) return [];
    
    const total = userStats.totalUsers || 1; // Avoid division by zero
    const breakdown = userStats.userStatusBreakdown || {};
    
    const data = [
      { 
        id: 'Active', 
        label: 'Active', 
        value: breakdown.active || 0, 
        color: '#10b981',
        description: 'Users with active accounts'
      },
      { 
        id: 'Trial', 
        label: 'Trial', 
        value: breakdown.trial || 0, 
        color: '#3b82f6',
        description: 'Users in trial period'
      },
      { 
        id: 'Paid', 
        label: 'Paid', 
        value: breakdown.paid || 0, 
        color: '#8b5cf6',
        description: 'Users with active subscriptions'
      },
      { 
        id: 'Idle', 
        label: 'Idle', 
        value: breakdown.idle || 0, 
        color: '#f59e0b',
        description: 'Users with low activity'
      },
      { 
        id: 'Inactive', 
        label: 'Inactive', 
        value: breakdown.inactive || 0, 
        color: '#ef4444',
        description: 'Inactive users'
      }
    ].filter(item => item.value > 0).map(item => ({
      ...item,
      percentage: total > 0 ? Math.round((item.value / total) * 100) : 0
    }));
    
    return data;
  }, [userStats]);

  // Nivo line data for users
  const nivoUserLineData = useMemo(() => [
    {
      id: chartView === 'monthly' ? 'Monthly Users' : 'Daily Users',
      color: 'hsl(229, 70%, 50%)',
      data: userChartData.labels.map((label, idx) => ({
        x: label,
        y: userChartData.values[idx],
        rawDate: userChartData.rawLabels?.[idx] || label // Store raw date for better tooltip
      }))
    }
  ], [userChartData, chartView]);

  // Top organizations by user count bar data
  const topOrgsBarData = useMemo(() => {
    if (!userStats?.topOrgsByUsers) return [];
    return userStats.topOrgsByUsers.map(org => ({
      organization: org.organizationName,
      Users: org.userCount
    }));
  }, [userStats]);

  return (
    <div className="space-y-6 custom-scrollbar">
      {/* Toggle Switch */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">Dashboard</h2>
        <div className="flex items-center space-x-2 bg-bg-50 p-1 rounded-lg">
          <button
            onClick={() => setViewMode('organizations')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center ${
              viewMode === 'organizations'
                ? 'bg-ac-02 text-white shadow-sm'
                : 'text-fg-60 hover:bg-bg-40'
            }`}
          >
            <Building2 size={16} className="mr-2" />
            Organizations
          </button>
          <button
            onClick={() => setViewMode('users')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center ${
              viewMode === 'users'
                ? 'bg-ac-02 text-white shadow-sm'
                : 'text-fg-60 hover:bg-bg-40'
            }`}
          >
            <Users size={16} className="mr-2" />
            Users
          </button>
        </div>
      </div>

      {/* Organization Statistics View */}
      {viewMode === 'organizations' && (
        <>
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Organizations */}
        <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-fg-60">Total Organizations</p>
              <h3 className="text-2xl font-semibold mt-1">{totalOrganizations}</h3>
              <p className="text-xs text-fg-60 mt-1">
                {activeOrganizations} active
              </p>
            </div>
            <div className="p-3 rounded-full bg-bg-50">
              <Building2 size={24} className="text-ac-02" />
            </div>
          </div>
        </div>

        {/* Total Employees */}
        <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-fg-60">Total Employees</p>
              <h3 className="text-2xl font-semibold mt-1">{totalEmployees.toLocaleString()}</h3>
              <p className="text-xs text-fg-60 mt-1">
                {avgEmployees} avg per organization
              </p>
            </div>
            <div className="p-3 rounded-full bg-bg-50">
              <Users size={24} className="text-green-500" />
            </div>
          </div>
        </div>

        {/* Active Organizations */}
        <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-fg-60">Active Organizations</p>
              <h3 className="text-2xl font-semibold mt-1">{activePercentage}%</h3>
              <p className="text-xs text-fg-60 mt-1">
                {activeOrganizations} of {totalOrganizations}
              </p>
            </div>
            <div className="p-3 rounded-full bg-bg-50">
              <Activity size={24} className="text-blue-500" />
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-fg-60">Recent Activity</p>
              <h3 className="text-2xl font-semibold mt-1">
                {recentOrgs.length}
              </h3>
              <p className="text-xs text-fg-60 mt-1">
                New organizations
              </p>
            </div>
            <div className="p-3 rounded-full bg-bg-50">
              <Clock size={24} className="text-purple-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Time-based Organizations Chart (Nivo) */}
        <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium flex items-center">
              <TrendingUp size={20} className="mr-2 text-blue-500" />
              {chartView === 'monthly' ? 'Monthly' : 'Daily'} Organizations
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setChartView('daily')}
                className={`px-3 py-1 text-sm rounded-md flex items-center ${
                  chartView === 'daily' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-bg-50 text-fg-60 hover:bg-bg-40'
                }`}
              >
                <Calendar size={16} className="mr-1" />
                Daily
              </button>
              <button
                onClick={() => setChartView('monthly')}
                className={`px-3 py-1 text-sm rounded-md flex items-center ${
                  chartView === 'monthly'
                    ? 'bg-blue-500 text-white' 
                    : 'bg-bg-50 text-fg-60 hover:bg-bg-40'
                }`}
              >
                <CalendarDays size={16} className="mr-1" />
                Monthly
              </button>
            </div>
          </div>
          <div className="h-64" style={{ height: 320 }}>
            <ResponsiveLine
              data={nivoLineData}
              margin={{ top: 30, right: 40, bottom: 60, left: 50 }}
              xScale={{ type: 'point' }}
              yScale={{
                type: 'linear',
                min: 0,
                max: 'auto',
                stacked: false,
                reverse: false
              }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                orient: 'bottom',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: chartView === 'monthly' ? 'Month' : 'Date',
                legendOffset: 36,
                legendPosition: 'middle'
              }}
              axisLeft={{
                orient: 'left',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Organizations',
                legendOffset: -40,
                legendPosition: 'middle'
              }}
              colors={{ scheme: 'category10' }}
              pointSize={10}
              pointColor={{ theme: 'background' }}
              pointBorderWidth={2}
              pointBorderColor={{ from: 'serieColor' }}
              pointLabelYOffset={-12}
              useMesh={true}
              enableArea={true}
              areaOpacity={0.15}
              theme={{
                axis: {
                  ticks: {
                    text: { fill: '#9CA3AF' }
                  },
                  legend: {
                    text: { fill: '#9CA3AF' }
                  }
                },
                tooltip: {
                  container: {
                    background: '#22223b',
                    color: '#fff',
                    fontSize: 14
                  }
                }
              }}
              tooltip={({ point }) => (
                <div className="p-2 bg-bg-80 rounded shadow text-xs">
                  <strong>{point.data.xFormatted}</strong>: {point.data.yFormatted}
                </div>
              )}
            />
          </div>
        </div>
        {/* Organization Status (Nivo Pie) */}
        <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium flex items-center">
              <PieChartIcon size={20} className="mr-2 text-green-500" />
              Organization Status
            </h3>
          </div>
          <div className="h-64 flex items-center justify-center">
            <div className="w-full max-w-xs" style={{ height: 256 }}>
              <ResponsivePie
                data={nivoPieData}
                margin={{ top: 30, right: 40, bottom: 30, left: 40 }}
                innerRadius={0.6}
                padAngle={1.5}
                cornerRadius={6}
                colors={({ data }) => data.color}
                borderWidth={2}
                borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                enableArcLabels={false}
                arcLinkLabelsSkipAngle={10}
                arcLinkLabelsTextColor="#9CA3AF"
                arcLinkLabelsThickness={2}
                arcLinkLabelsColor={{ from: 'color' }}
                legends={[
                  {
                    anchor: 'right',
                    direction: 'column',
                    justify: false,
                    translateX: 100,
                    translateY: 0,
                    itemsSpacing: 8,
                    itemWidth: 80,
                    itemHeight: 24,
                    itemTextColor: '#9CA3AF',
                    symbolSize: 18,
                    symbolShape: 'circle'
                  }
                ]}
                theme={{
                  tooltip: {
                    container: {
                      background: '#22223b',
                      color: '#fff',
                      fontSize: 14
                    }
                  }
                }}
                tooltip={({ datum }) => (
                  <div className="p-2 bg-bg-80 rounded shadow text-xs">
                    <strong>{datum.label}</strong>: {datum.value}
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Employee Distribution Chart (Nivo Bar) */}
      {employeeDistribution.length > 0 && (
        <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium flex items-center">
              <BarChartIcon size={20} className="mr-2 text-purple-500" />
              Top Organizations by Employee Count
            </h3>
          </div>
          <div className="h-80" style={{ height: 320 }}>
            <ResponsiveBar
              data={nivoBarData}
              keys={['Employees']}
              indexBy="organization"
              margin={{ top: 30, right: 40, bottom: 60, left: 80 }}
              padding={0.3}
              layout="horizontal"
              colors={{ scheme: 'category10' }}
              borderRadius={4}
              borderWidth={2}
              borderColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Employees',
                legendPosition: 'middle',
                legendOffset: 40
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Organization',
                legendPosition: 'middle',
                legendOffset: -60
              }}
              enableLabel={false}
              theme={{
                axis: {
                  ticks: { text: { fill: '#9CA3AF' } },
                  legend: { text: { fill: '#9CA3AF' } }
                },
                tooltip: {
                  container: {
                    background: '#22223b',
                    color: '#fff',
                    fontSize: 14
                  }
                }
              }}
              tooltip={({ id, value, indexValue }) => (
                <div className="p-2 bg-bg-80 rounded shadow text-xs">
                  <strong>{indexValue}</strong>: {value}
                </div>
              )}
            />
          </div>
        </div>
      )}

      {/* Recent Organizations */}
      <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Recent Organizations</h2>
        </div>
        {recentOrgs.length > 0 ? (
          <div className="space-y-4">
            {recentOrgs.map((org) => (
              <div key={org.id} className="flex items-center justify-between pb-4 border-b border-bd-50 last:border-0 last:pb-0">
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-bg-50 flex items-center justify-center mr-3">
                    <Building2 size={16} className="text-fg-60" />
                  </div>
                  <div>
                    <h3 className="font-medium">{org.name}</h3>
                    <p className="text-xs text-fg-60">{org.domain}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-fg-60">
                    Created: {formatDateTime(org.createdAt)}
                  </p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    org.status 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {org.status ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-fg-60">No organizations found</p>
          </div>
        )}
      </div>
        </>
      )}

      {/* User Statistics View */}
      {viewMode === 'users' && (
        <>
          {loadingUserStats ? (
            <div className="text-center py-12">
              <p className="text-fg-60">Loading user statistics...</p>
            </div>
          ) : userStats ? (
            <>
              {/* User Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Total Users */}
                <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-fg-60">Total Users</p>
                      <h3 className="text-2xl font-semibold mt-1">{userStats.totalUsers?.toLocaleString() || 0}</h3>
                      <p className="text-xs text-fg-60 mt-1">
                        {userStats.activeUsers || 0} active
                      </p>
                    </div>
                    <div className="p-3 rounded-full bg-bg-50">
                      <Users size={24} className="text-ac-02" />
                    </div>
                  </div>
                </div>

                {/* New Users */}
                <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-fg-60">New Users</p>
                      <h3 className="text-2xl font-semibold mt-1">{userStats.newUsers?.toLocaleString() || 0}</h3>
                      <p className="text-xs text-fg-60 mt-1">
                        Last 30 days
                      </p>
                    </div>
                    <div className="p-3 rounded-full bg-bg-50">
                      <UserPlus size={24} className="text-green-500" />
                    </div>
                  </div>
                </div>

                {/* Trial Users */}
                <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-fg-60">Trial Users</p>
                      <h3 className="text-2xl font-semibold mt-1">{userStats.trialUsers?.toLocaleString() || 0}</h3>
                      <p className="text-xs text-fg-60 mt-1">
                        Active trials
                      </p>
                    </div>
                    <div className="p-3 rounded-full bg-bg-50">
                      <UserCheck size={24} className="text-blue-500" />
                    </div>
                  </div>
                </div>

                {/* Paid Users */}
                <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-fg-60">Paid Users</p>
                      <h3 className="text-2xl font-semibold mt-1">{userStats.paidUsers?.toLocaleString() || 0}</h3>
                      <p className="text-xs text-fg-60 mt-1">
                        Active subscriptions
                      </p>
                    </div>
                    <div className="p-3 rounded-full bg-bg-50">
                      <CreditCard size={24} className="text-purple-500" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional User Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Active Users */}
                <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-fg-60">Active Users</p>
                      <h3 className="text-2xl font-semibold mt-1">{userStats.activeUsers?.toLocaleString() || 0}</h3>
                      <p className="text-xs text-fg-60 mt-1">
                        Last 30 days
                      </p>
                    </div>
                    <div className="p-3 rounded-full bg-bg-50">
                      <Activity size={24} className="text-green-500" />
                    </div>
                  </div>
                </div>

                {/* Idle Users */}
                <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-fg-60">Idle Users</p>
                      <h3 className="text-2xl font-semibold mt-1">{userStats.idleUsers?.toLocaleString() || 0}</h3>
                      <p className="text-xs text-fg-60 mt-1">
                        Low activity
                      </p>
                    </div>
                    <div className="p-3 rounded-full bg-bg-50">
                      <UserX size={24} className="text-orange-500" />
                    </div>
                  </div>
                </div>

                {/* Churn Rate */}
                <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-fg-60">Churn Rate</p>
                      <h3 className="text-2xl font-semibold mt-1">{userStats.churnRate || 0}%</h3>
                      <p className="text-xs text-fg-60 mt-1">
                        Cancelled subscriptions
                      </p>
                    </div>
                    <div className="p-3 rounded-full bg-bg-50">
                      <TrendingUp size={24} className="text-red-500" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Daily/Monthly Users Chart */}
                <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium flex items-center">
                      <TrendingUp size={20} className="mr-2 text-blue-500" />
                      {chartView === 'monthly' ? 'Monthly' : 'Daily'} New Users
                    </h3>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setChartView('daily')}
                        className={`px-3 py-1 text-sm rounded-md flex items-center ${
                          chartView === 'daily' 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-bg-50 text-fg-60 hover:bg-bg-40'
                        }`}
                      >
                        <Calendar size={16} className="mr-1" />
                        Daily
                      </button>
                      <button
                        onClick={() => setChartView('monthly')}
                        className={`px-3 py-1 text-sm rounded-md flex items-center ${
                          chartView === 'monthly'
                            ? 'bg-blue-500 text-white' 
                            : 'bg-bg-50 text-fg-60 hover:bg-bg-40'
                        }`}
                      >
                        <CalendarDays size={16} className="mr-1" />
                        Monthly
                      </button>
                    </div>
                  </div>
                  <div className="h-64" style={{ height: 320 }}>
                    {userChartData.labels.length > 0 ? (
                      <ResponsiveLine
                        data={nivoUserLineData}
                        margin={{ top: 30, right: 40, bottom: 60, left: 50 }}
                        xScale={{ type: 'point' }}
                        yScale={{
                          type: 'linear',
                          min: 0,
                          max: 'auto',
                          stacked: false,
                          reverse: false
                        }}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                          orient: 'bottom',
                          tickSize: 5,
                          tickPadding: 5,
                          tickRotation: chartView === 'daily' ? -45 : 0,
                          legend: chartView === 'monthly' ? 'Month' : 'Date',
                          legendOffset: 50,
                          legendPosition: 'middle',
                          tickValues: chartView === 'daily' && userChartData.labels.length > 15
                            ? userChartData.labels.filter((_, idx) => idx % Math.ceil(userChartData.labels.length / 10) === 0)
                            : undefined // Show all labels if less than 15, or for monthly view
                        }}
                        axisLeft={{
                          orient: 'left',
                          tickSize: 5,
                          tickPadding: 5,
                          tickRotation: 0,
                          legend: 'Users',
                          legendOffset: -40,
                          legendPosition: 'middle'
                        }}
                        colors={{ scheme: 'category10' }}
                        pointSize={10}
                        pointColor={{ theme: 'background' }}
                        pointBorderWidth={2}
                        pointBorderColor={{ from: 'serieColor' }}
                        pointLabelYOffset={-12}
                        useMesh={true}
                        enableArea={true}
                        areaOpacity={0.15}
                        theme={{
                          axis: {
                            ticks: {
                              text: { fill: '#9CA3AF' }
                            },
                            legend: {
                              text: { fill: '#9CA3AF' }
                            }
                          },
                          tooltip: {
                            container: {
                              background: '#22223b',
                              color: '#fff',
                              fontSize: 14
                            }
                          }
                        }}
                        tooltip={({ point }) => (
                          <div className="p-2 bg-bg-80 rounded shadow text-xs">
                            <strong>{point.data.xFormatted}</strong>: {point.data.yFormatted} {point.data.yFormatted === '1' ? 'user' : 'users'}
                          </div>
                        )}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-fg-60">
                        No user data available
                      </div>
                    )}
                  </div>
                </div>

                {/* User Status Pie Chart */}
                <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium flex items-center">
                      <PieChartIcon size={20} className="mr-2 text-green-500" />
                      User Status Breakdown
                    </h3>
                  </div>
                  <div className="flex items-center justify-center">
                    {userStatusPieData.length > 0 ? (
                      <div className="w-full flex flex-col lg:flex-row items-center justify-center gap-6">
                        {/* Chart */}
                        <div className="w-full max-w-sm" style={{ height: 300 }}>
                          <ResponsivePie
                            data={userStatusPieData}
                            margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                            innerRadius={0.65}
                            padAngle={2}
                            cornerRadius={8}
                            colors={({ data }) => data.color}
                            borderWidth={3}
                            borderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
                            enableArcLabels={true}
                            arcLabel={(d) => {
                              // Calculate percentage from value and total
                              const total = userStats?.totalUsers || 1;
                              const percentage = total > 0 ? Math.round((d.value / total) * 100) : 0;
                              return percentage >= 5 ? `${percentage}%` : ''; // Only show if >= 5% to avoid clutter
                            }}
                            arcLabelsRadiusOffset={0.65}
                            arcLabelsSkipAngle={15}
                            arcLabelsTextColor="#ffffff"
                            arcLabelsTextOffset={5}
                            arcLabelsTextStyle={{
                              fontSize: 13,
                              fontWeight: 'bold',
                              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                            }}
                            arcLinkLabelsSkipAngle={10}
                            arcLinkLabelsTextColor="#9CA3AF"
                            arcLinkLabelsThickness={2}
                            arcLinkLabelsColor={{ from: 'color' }}
                            theme={{
                              labels: {
                                text: {
                                  fill: '#ffffff',
                                  fontSize: 12,
                                  fontWeight: 'bold'
                                }
                              },
                              tooltip: {
                                container: {
                                  background: '#1f2937',
                                  color: '#fff',
                                  fontSize: 13,
                                  borderRadius: '8px',
                                  padding: '10px',
                                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
                                }
                              }
                            }}
                            tooltip={({ datum }) => (
                              <div className="p-3 bg-bg-80 rounded-lg shadow-lg border border-bd-50">
                                <div className="flex items-center gap-2 mb-2">
                                  <div 
                                    className="w-4 h-4 rounded-full" 
                                    style={{ backgroundColor: datum.color }}
                                  />
                                  <strong className="text-sm font-semibold">{datum.label}</strong>
                                </div>
                                <div className="text-xs text-fg-40">
                                  <div className="mb-1">
                                    <span className="font-medium">{datum.value.toLocaleString()}</span> users
                                  </div>
                                  <div>
                                    <span className="font-medium">{datum.percentage}%</span> of total
                                  </div>
                                  {datum.description && (
                                    <div className="mt-2 text-fg-60 italic">
                                      {datum.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                        </div>
                        
                        {/* Enhanced Legend */}
                        <div className="flex flex-col gap-3 min-w-[200px]">
                          <h4 className="text-sm font-semibold text-fg-50 mb-2">Status Breakdown</h4>
                          {userStatusPieData.map((item) => (
                            <div key={item.id} className="flex items-center justify-between p-2 rounded-md hover:bg-bg-50 transition-colors">
                              <div className="flex items-center gap-3">
                                <div 
                                  className="w-4 h-4 rounded-full shadow-sm" 
                                  style={{ backgroundColor: item.color }}
                                />
                                <span className="text-sm font-medium text-fg-50">{item.label}</span>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-semibold text-fg-50">{item.value.toLocaleString()}</div>
                                <div className="text-xs text-fg-60">{item.percentage}%</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-fg-60">No user status data available</p>
                        <p className="text-xs text-fg-60 mt-2">User statistics will appear here once users are registered</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Top Organizations by User Count */}
              {topOrgsBarData.length > 0 && (
                <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium flex items-center">
                      <BarChartIcon size={20} className="mr-2 text-purple-500" />
                      Top Organizations by User Count
                    </h3>
                  </div>
                  <div className="h-80" style={{ height: 320 }}>
                    <ResponsiveBar
                      data={topOrgsBarData}
                      keys={['Users']}
                      indexBy="organization"
                      margin={{ top: 30, right: 40, bottom: 60, left: 80 }}
                      padding={0.3}
                      layout="horizontal"
                      colors={{ scheme: 'category10' }}
                      borderRadius={4}
                      borderWidth={2}
                      borderColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Users',
                        legendPosition: 'middle',
                        legendOffset: 40
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Organization',
                        legendPosition: 'middle',
                        legendOffset: -60
                      }}
                      enableLabel={false}
                      theme={{
                        axis: {
                          ticks: { text: { fill: '#9CA3AF' } },
                          legend: { text: { fill: '#9CA3AF' } }
                        },
                        tooltip: {
                          container: {
                            background: '#22223b',
                            color: '#fff',
                            fontSize: 14
                          }
                        }
                      }}
                      tooltip={({ id, value, indexValue }) => (
                        <div className="p-2 bg-bg-80 rounded shadow text-xs">
                          <strong>{indexValue}</strong>: {value} users
                        </div>
                      )}
                    />
                  </div>
                </div>
              )}

              {/* Recent Users */}
              <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Recent Users</h2>
                </div>
                {userStats.recentUsers && userStats.recentUsers.length > 0 ? (
                  <div className="space-y-4">
                    {userStats.recentUsers.map((user) => (
                      <div key={user._id} className="flex items-center justify-between pb-4 border-b border-bd-50 last:border-0 last:pb-0">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-bg-50 flex items-center justify-center mr-3">
                            <Users size={16} className="text-fg-60" />
                          </div>
                          <div>
                            <h3 className="font-medium">{user.name}</h3>
                            <p className="text-xs text-fg-60">{user.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-fg-60">
                            Created: {formatDateTime(user.createdAt)}
                          </p>
                          <div className="flex items-center justify-end gap-2 mt-1">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.status 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {user.status ? 'Active' : 'Inactive'}
                            </span>
                            {user.trialStatus && user.role === 'individual' && (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                user.trialStatus === 'progress' 
                                  ? 'bg-blue-100 text-blue-800'
                                  : user.trialStatus === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : user.trialStatus === 'expired'
                                  ? 'bg-red-100 text-red-800'
                                  : user.trialStatus === 'paid'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                Trial: {user.trialStatus.charAt(0).toUpperCase() + user.trialStatus.slice(1)}
                              </span>
                            )}
                            {user.role === 'employee' && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                Employee
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-fg-60">No users found</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-fg-60">Failed to load user statistics. Please try again.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
